// The WhatsApp side of the MSG91 account, with the plain-English "would a
// message actually be delivered right now?" banner first — the same layout as
// the SMS Gateway's Connection tab, for the same reason: everything can be
// filled in correctly and still deliver nothing while the switches are off.
//
// The auth key is normally NOT entered here. WhatsApp and SMS are one MSG91
// account, so the key saved on the SMS Gateway screen is used; this screen
// offers an optional WhatsApp-only override.
import { useEffect, useState } from 'react';
import { MessageCircle, ShieldAlert, TriangleAlert, CircleCheck } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Switch } from '@/components/ui/Switch';
import { FormField } from '@/components/ui/FormField';
import { LoadingState, ErrorState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { useWhatsAppGateway, useSaveWhatsAppGateway, useWhatsAppTestConnection } from './hooks';
import type { WhatsAppSettingsInput, WhatsAppGatewaySettings } from './types';

type Draft = WhatsAppSettingsInput;

const fromSettings = (s: WhatsAppGatewaySettings): Draft => ({
  integrated_number: s.integrated_number,
  country_code: s.country_code,
  send_enabled: s.send_enabled,
  simulate_only: s.simulate_only,
  auth_key: '',
});

const KEY_SOURCE: Record<string, string> = {
  whatsapp: 'Using a WhatsApp-only key saved on this screen.',
  sms: 'Using the MSG91 key saved under SMS → Connection (same MSG91 account).',
  environment: 'Using the key from the server environment (MSG91_AUTH_KEY).',
};

export default function ConnectionTab() {
  const gateway = useWhatsAppGateway();
  const save = useSaveWhatsAppGateway();
  const test = useWhatsAppTestConnection();

  const [draft, setDraft] = useState<Draft>({});
  const [showKey, setShowKey] = useState(false);
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Not re-seeded while there are unsaved edits — see the SMS ConnectionTab's
  // note: a window-focus refetch would otherwise wipe a half-filled form.
  useEffect(() => {
    if (gateway.data && !dirty) setDraft(fromSettings(gateway.data.settings));
  }, [gateway.data, dirty]);

  if (gateway.isLoading) return <LoadingState label="Loading WhatsApp gateway settings…" />;
  if (gateway.isError) {
    return (
      <ErrorState
        message={gateway.error instanceof ApiError ? gateway.error.message : 'Could not load WhatsApp gateway settings'}
        onRetry={() => gateway.refetch()}
      />
    );
  }

  const status = gateway.data!.status;
  const settings = gateway.data!.settings;
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => {
    setDirty(true);
    setDraft((p) => ({ ...p, [k]: v }));
  };

  // An empty key field means "leave the stored key alone".
  const payload = (): Draft => {
    const { auth_key, ...rest } = draft;
    return auth_key ? { ...rest, auth_key } : rest;
  };

  const onSave = async () => {
    const res = await save.mutateAsync(payload());
    setDraft(fromSettings(res.settings));
    setDirty(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2200);
  };

  const onClearOverride = async () => {
    const res = await save.mutateAsync({ clear_auth_key: true });
    setDraft(fromSettings(res.settings));
    setDirty(false);
  };

  const onTest = async () => {
    const res = await test.mutateAsync(payload());
    setDraft((p) => (res.settings ? fromSettings(res.settings) : { ...p, auth_key: '' }));
    setDirty(false);
  };

  const busy = save.isPending || test.isPending;
  const result = test.data;
  const live = status.can_send && !status.simulate_only;

  return (
    <div className="max-w-3xl space-y-4">
      <div
        className={
          'rounded-xl border p-4 ' +
          (live ? 'border-success-soft bg-success-soft' : status.can_send ? 'border-warn-soft bg-warn-soft' : 'border-danger-soft bg-danger-soft')
        }
      >
        <div className="flex items-start gap-2.5">
          {live ? (
            <CircleCheck size={17} className="mt-px shrink-0 text-success-ink" />
          ) : status.can_send ? (
            <TriangleAlert size={17} className="mt-px shrink-0 text-warn-ink" />
          ) : (
            <ShieldAlert size={17} className="mt-px shrink-0 text-danger-ink" />
          )}
          <div className="min-w-0">
            <div className={'text-[13px] font-semibold ' + (live ? 'text-success-ink' : status.can_send ? 'text-warn-ink' : 'text-danger-ink')}>
              {status.summary}
            </div>
            {status.blockers.length > 1 && (
              <ul className="mt-2 list-disc space-y-1 pl-4 text-[12px] text-danger-ink">
                {status.blockers.slice(1).map((b) => <li key={b.code}>{b.message}</li>)}
              </ul>
            )}
            {status.warnings.filter((w) => w.code !== 'simulate_only').map((w) => (
              <div key={w.code} className="mt-2 text-[12px] text-warn-ink">⚠ {w.message}</div>
            ))}
            <div className="mt-2 text-[12px] text-ink-muted">
              {status.consent && !status.consent.enforced
                ? 'The “Receive messages on WhatsApp” consent form is disabled, so WhatsApp is sent to every patient except those who have declined it.'
                : 'WhatsApp is sent only to patients who have accepted the “Receive messages on WhatsApp” consent. Declined or not yet asked means not sent.'}{' '}
              Change this under Clinical Setup → Consent Forms. It is independent of SMS — neither channel falls
              back to the other.
            </div>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle><MessageCircle size={15} /> MSG91 WhatsApp</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid2">
            <FormField
              label="Integrated number"
              error={save.error instanceof ApiError ? save.error.fieldErrors.integrated_number : undefined}
            >
              <Input
                value={draft.integrated_number ?? ''}
                onChange={(e) => set('integrated_number', e.target.value)}
                placeholder="919876543210"
                inputMode="numeric"
              />
              <div className="mt-1 text-[11px] text-ink-muted">
                The verified WhatsApp Business number shown in the MSG91 WhatsApp dashboard, with country code.
                {settings.integrated_number_source === 'environment' && ' Currently taken from MSG91_WHATSAPP_INTEGRATED_NUMBER.'}
              </div>
            </FormField>

            <FormField
              label="Country code"
              error={save.error instanceof ApiError ? save.error.fieldErrors.country_code : undefined}
            >
              <Input
                value={draft.country_code ?? '91'}
                onChange={(e) => set('country_code', e.target.value)}
                placeholder="91"
                maxLength={4}
              />
              <div className="mt-1 text-[11px] text-ink-muted">Added to a patient’s 10-digit mobile number.</div>
            </FormField>
          </div>

          <FormField label="Auth key (optional override)">
            <div className="relative">
              <Input
                type={showKey ? 'text' : 'password'}
                value={draft.auth_key ?? ''}
                onChange={(e) => set('auth_key', e.target.value)}
                placeholder={settings.auth_key_override_set ? 'A WhatsApp-only key is saved — type here only to replace it' : 'Leave blank to use the key saved under SMS → Connection'}
                autoComplete="off"
              />
              <button
                type="button"
                onClick={() => setShowKey((p) => !p)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-ink-muted hover:text-ink"
              >
                {showKey ? 'Hide' : 'Show'}
              </button>
            </div>
            <div className="mt-1 text-[11px] text-ink-muted">
              {settings.auth_key_set
                ? KEY_SOURCE[settings.auth_key_source ?? ''] ?? 'A key is configured.'
                : 'No MSG91 key is configured anywhere yet. Save it under SMS → Connection.'}{' '}
              The key is never sent back to this screen.
            </div>
            {settings.auth_key_override_set && (
              <Button size="sm" variant="ghost" className="mt-2" onClick={onClearOverride} disabled={busy}>
                Remove override and use the SMS key
              </Button>
            )}
          </FormField>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Delivery switches</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[13px] font-semibold">Send WhatsApp</div>
              <div className="mt-0.5 text-[12px] text-ink-muted">
                The master switch. While it is off, nothing is sent — every attempt is still logged with the reason.
              </div>
            </div>
            <Switch checked={!!draft.send_enabled} onCheckedChange={(v) => set('send_enabled', v)} aria-label="Send WhatsApp" />
          </label>

          <label className="flex items-start justify-between gap-4 border-t border-border pt-4">
            <div>
              <div className="text-[13px] font-semibold">Simulate only</div>
              <div className="mt-0.5 text-[12px] text-ink-muted">
                Build and verify every message, then stop. Turn this off last, once a test message has arrived on a
                staff phone.
              </div>
            </div>
            <Switch checked={!!draft.simulate_only} onCheckedChange={(v) => set('simulate_only', v)} aria-label="Simulate only" />
          </label>
        </CardContent>
      </Card>

      {result && (
        <div
          className={
            'rounded-xl border p-3 text-[13px] ' +
            (result.ok ? 'border-success-soft bg-success-soft text-success-ink' : 'border-danger-soft bg-danger-soft text-danger-ink')
          }
        >
          {result.ok ? (
            <>
              ✓ Connected to MSG91 for <strong>{result.integrated_number}</strong>. {result.template_count} WhatsApp
              template{result.template_count === 1 ? '' : 's'} found on the account.
              {!!result.templates?.length && (
                <ul className="mt-2 list-disc space-y-0.5 pl-4 text-[12px]">
                  {result.templates.slice(0, 10).map((t, i) => (
                    <li key={`${t.name}-${i}`}>
                      <code>{t.name}</code>{t.language ? ` · ${t.language}` : ''}{t.status ? ` · ${t.status}` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <>✗ {result.error}</>
          )}
        </div>
      )}
      {test.isError && (
        <div className="rounded-xl border border-danger-soft bg-danger-soft p-3 text-[13px] text-danger-ink">
          ✗ {test.error instanceof ApiError ? test.error.message : 'The connection test could not be run.'}
        </div>
      )}
      {save.isError && !(save.error instanceof ApiError && Object.keys(save.error.fieldErrors).length) && (
        <div className="rounded-xl border border-danger-soft bg-danger-soft p-3 text-[13px] text-danger-ink">
          ✗ {save.error instanceof ApiError ? save.error.message : 'Could not save.'}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button onClick={onSave} loading={save.isPending} disabled={busy}>
          {saved ? '✓ Saved' : 'Save settings'}
        </Button>
        <Button variant="ghost" onClick={onTest} loading={test.isPending} disabled={busy}>
          ⚡ Save &amp; test connection
        </Button>
      </div>

      {settings.updated_at && (
        <div className="text-[11px] text-ink-muted">
          Last changed {new Date(settings.updated_at).toLocaleString('en-IN')}
          {settings.updated_by ? ` by ${settings.updated_by}` : ''}.
        </div>
      )}
    </div>
  );
}
